import { Fragment, useEffect, useRef, forwardRef, useImperativeHandle, type MutableRefObject } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../core';
import { FAIcon } from '../Icons/Icons';
import { KeyConstants } from '../Accordion/types/enums';
import styles from './Menu.module.scss';
import { type MenuProps, type MenuItem } from './types/propTypes';
import { MenuItemIconAlignement } from './types/enums';

const cx = classNames.bind(styles);

/**
 * @description Menu component provides a basic menu with list items.
 * The width of menu is set based on its text content and icon (if any).
 * To change the default width or menu position, pass a "className" and customize.
 * Props ->
 * menuItems: Array of list item objects.
 * onClose: callback to call when click outside the Menu.
 * className: prop to apply custom styling.
 */
const Menu = forwardRef<HTMLDivElement, MenuProps>(({ menuItems, className, onClose }: MenuProps, ref) => {
  const menuRef = useRef() as MutableRefObject<HTMLDivElement>;
  useImperativeHandle(ref, () => menuRef.current);
  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent | KeyboardEvent) => {
      if (!menuRef.current?.contains(e.target as HTMLElement)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        checkIfClickedOutside(e);
      }
    });
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
      document.removeEventListener('keydown', checkIfClickedOutside);
    };
  }, []);
  const keys = [KeyConstants.ENTER, KeyConstants.SPACE];

  return (
    <div className={`${styles['menu-container']} ${className || ''}`} ref={menuRef}>
      <ul>
        {menuItems.map(
          ({ disabled, id, icon, iconProps, onClick, title, iconAlignment = MenuItemIconAlignement.RIGHT }, index) => {
            const MenuItenIconClassname = cx('icon', [iconAlignment]);
            return (
              <Fragment key={id}>
                {index > 0 && <hr className={styles.divider} />}
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                <li
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex={0}
                  className={disabled ? styles.disabled : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                      onClick?.(e);
                    }
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (!disabled && keys.includes(e.code as KeyConstants)) {
                      onClick(e);
                    }
                  }}
                  data-testid="menu-item-testid"
                >
                  <div className={styles.main}>
                    {iconAlignment !== MenuItemIconAlignement.RIGHT && (
                      <FAIcon {...optionalProp('iconName', icon)} className={MenuItenIconClassname} {...iconProps} />
                    )}
                    <span className={styles.title}>{title}</span>
                  </div>
                  {iconAlignment !== MenuItemIconAlignement.LEFT && (
                    <FAIcon {...optionalProp('iconName', icon)} className={MenuItenIconClassname} {...iconProps} />
                  )}
                </li>
              </Fragment>
            );
          },
        )}
      </ul>
    </div>
  );
});
Menu.displayName = 'Menu';
export default Menu;
export { MenuItemIconAlignement };
export type { MenuProps, MenuItem };
